import { LocalStorageKeys } from "@/common/constants";
import type { JwtTokenObtainPair, MfaMethod } from "@/types/auth/auth";
import authApi from "@/types/auth/authApi";
import type { CurrentUserRead } from "@/types/user/user";
import userApi from "@/types/user/userApi";
import { callApi } from "@/Utils/request/query";
import { useRef, useState } from "react";
import { refreshSession } from "./refreshSession";
import { isSessionOwner } from "./sessionRecovery";

export function useSessionReauthentication(
  user: CurrentUserRead,
  recovered: (user: CurrentUserRead, access: string) => void,
) {
  const inFlight = useRef(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [differentUser, setDifferentUser] = useState(false);
  const [tempToken, setTempToken] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [method, setMethod] = useState<MfaMethod>("totp");
  const verifyOwner = async (tokens?: JwtTokenObtainPair) => {
    const access =
      tokens?.access ?? localStorage.getItem(LocalStorageKeys.accessToken);
    if (!access) throw new Error("No access token");
    const verified = await callApi(
      { ...userApi.currentUser, noAuth: true },
      {
        headers: { Authorization: `Bearer ${access}` },
        silent: true,
        signal: AbortSignal.timeout(15000),
      },
    );
    if (!verified.id || verified.id !== user.id) {
      setDifferentUser(true);
      return;
    }
    // Do not accept a concurrent tab's credentials without checking them.
    if (
      !tokens &&
      localStorage.getItem(LocalStorageKeys.accessToken) !== access
    ) {
      throw new Error("Session changed during verification");
    }
    if (!isSessionOwner(user.id)) return;
    if (tokens) {
      localStorage.setItem(LocalStorageKeys.refreshToken, tokens.refresh);
      localStorage.setItem(LocalStorageKeys.accessToken, tokens.access);
    }
    recovered(verified, access);
  };
  const run = async (operation: () => Promise<void>) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setFailed(false);
    setDifferentUser(false);
    try {
      await operation();
    } catch {
      setFailed(true);
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };
  const retry = () =>
    run(async () => {
      await refreshSession();
      await verifyOwner();
    });
  const login = () =>
    run(async () => {
      const response = tempToken
        ? await callApi(
            { ...authApi.mfa.login, noAuth: true },
            {
              body: { temp_token: tempToken, code, method },
              silent: true,
              signal: AbortSignal.timeout(15000),
            },
          )
        : await callApi(authApi.login, {
            body: { username: user.username, password },
            silent: true,
            signal: AbortSignal.timeout(15000),
          });
      setPassword("");
      setCode("");
      if ("temp_token" in response) {
        setTempToken(response.temp_token);
        return;
      }
      if (
        typeof response.access !== "string" ||
        typeof response.refresh !== "string"
      ) {
        throw new Error("Invalid login response");
      }
      await verifyOwner(response);
    });
  return {
    busy,
    failed,
    differentUser,
    tempToken,
    password,
    setPassword,
    code,
    setCode,
    method,
    setMethod,
    retry,
    login,
  };
}
